import { createContext, Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  DealJourneyNavigatorPages,
  defaultFormValues,
  OrganisationTypeConstants,
  SessionStorageConstants,
} from "../../../constants";
import { DealDetailsFormValues } from "../../../models";
import { createDeal } from "../../../service/fund";
import { createCollaborator } from "../../../service/organisation";
import { createProject } from "../../../service/project";
import { ServiceError, Status } from "../../../service/Shared";
import { applyWorkflowTemplate } from "../../../service/workflow/WorkflowService.full";
import { useAuth } from "../../../useAuth";
import {
  mapDealWorkflowStageDates,
  parsePercentageInputNullable,
  safeSessionStorage,
  useFieldSetter,
} from "../../../utils";
import { getDealListingRoute } from "../../../utils/routes";
import { Toast } from "../../../widget";
import { getEmptyDealSpecificData } from "../utils/dealSpecificDataUtils";
import {
  getAssociationFromSearchParams,
  getStoredState,
  mergeAssociationIntoFormValues,
  removeStoredState,
  setStoredState,
} from "./utils";
import { mapInvestmentsToDealSpecificData } from "./utils/dealSpecificDataUtils";

interface ProjectProviderProps {
  children?: JSX.Element;
}

interface NewDealJourneyContextType {
  showHoldingPage: boolean;
  inProgressPage: DealJourneyNavigatorPages;
  currentPage: DealJourneyNavigatorPages;
  errors: ServiceError[] | undefined;
  formValues: DealDetailsFormValues;
  setFormValues: Dispatch<SetStateAction<DealDetailsFormValues>>;
  showDraftModal: boolean;
  handleUseDraft: () => void;
  handleStartFresh: () => void;
  handleFieldChange: <K extends keyof DealDetailsFormValues>(field: K) => (value: DealDetailsFormValues[K]) => void;
  getNextPage: (page: DealJourneyNavigatorPages) => DealJourneyNavigatorPages | null;
  handlePageChange: (page: DealJourneyNavigatorPages) => void;
  handleValidateAndNext: (saveData: boolean) => Promise<boolean>;
  handleCreateDeal: () => Promise<void>;
}

export const NewDealJourneyContext = createContext<NewDealJourneyContextType>({} as NewDealJourneyContextType);

export const NewDealJourneyProvider = ({ children }: ProjectProviderProps): JSX.Element => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentOrganisationUuid } = useAuth();

  const initialDraftRaw = useMemo(() => safeSessionStorage.getItem(SessionStorageConstants.Deal_Journey_Data), []);
  const storedState = useMemo(() => getStoredState(), []);

  const [currentPage, setCurrentPage] = useState<DealJourneyNavigatorPages>(storedState.inProgressPage); // Tracks which page the user is on
  const [inProgressPage, setInProgressPage] = useState<DealJourneyNavigatorPages>(storedState.inProgressPage); // Tracks the last validated page

  const [showHoldingPage, setShowHoldingPage] = useState(false);
  const [formValues, setFormValues] = useState<DealDetailsFormValues>(storedState.formValues);
  const [errors, setErrors] = useState<ServiceError[]>();
  const [showDraftModal, setShowDraftModal] = useState(false);
  const hasCheckedDraft = useRef(false);

  const handleFieldChange = useFieldSetter(setFormValues);
  const getDealTeamWithLead = useCallback((dealTeamUuids?: string[], dealLeadUuid?: string): string[] => {
    if (!dealLeadUuid) {
      return dealTeamUuids ?? [];
    }

    return Array.from(new Set([...(dealTeamUuids ?? []), dealLeadUuid]));
  }, []);

  const handlePageChange = useCallback((page: DealJourneyNavigatorPages): void => {
    setErrors([]);
    setCurrentPage(page);
  }, []);

  const getNextPage = useCallback((page: DealJourneyNavigatorPages): DealJourneyNavigatorPages | null => {
    const pages = Object.values(DealJourneyNavigatorPages);
    const index = pages.findIndex((value) => value === page);
    return pages[index + 1] ?? null;
  }, []);

  const handleValidateAndNext = useCallback(
    async (saveData: boolean): Promise<boolean> => {
      setErrors([]);

      // NOTE-JWI: When we complete the final stage, we need to create the objects in the correct order (need to create draft associations before creating deal)
      // NOTE_JWI: Landholding requires create group, can fully implement when we add landholdings extension here
      // Variables for mutating assiciations data after those records have been created
      const localCompanyDraftKey = formValues.companyDraft?.key;
      const landholdingProjectKeys = new Set(formValues.landholdingDrafts.flatMap((draft) => draft.projectKeys));
      const landholdingProjectUuids = new Set(
        formValues.projectDrafts
          .filter((draft) => landholdingProjectKeys.has(draft.key) && draft.projectUuid)
          .map((draft) => draft.projectUuid as string)
      );
      const persistedProjectKeyMap = formValues.projectDrafts.reduce(
        (accumulator, draft) => {
          if (draft.projectUuid && !landholdingProjectKeys.has(draft.key)) {
            accumulator[draft.key] = draft.projectUuid;
          }
          return accumulator;
        },
        {} as Record<string, string>
      );
      let fetchedCompanyKey: string | undefined;
      const fetchedProjectKeyMap: Record<string, string> = {};

      const localRelatedObjects = formValues.associations
        .filter(
          (association) =>
            !(
              association.objectType === "Project" &&
              (landholdingProjectKeys.has(association.objectUuid) ||
                landholdingProjectUuids.has(association.objectUuid))
            )
        )
        .map((association) => ({
          objectType: association.objectType,
          objectUuid: association.objectUuid,
        }));

      Object.values(persistedProjectKeyMap).forEach((projectUuid) => {
        const exists = localRelatedObjects.some(
          (relatedObject) => relatedObject.objectType === "Project" && relatedObject.objectUuid === projectUuid
        );

        if (!exists) {
          localRelatedObjects.push({
            objectType: "Project",
            objectUuid: projectUuid,
          });
        }
      });

      formValues.landholdingDrafts.forEach((landholdingDraft) => {
        const exists = localRelatedObjects.some(
          (relatedObject) =>
            relatedObject.objectType === "Landholding" && relatedObject.objectUuid === landholdingDraft.key
        );

        if (!exists) {
          localRelatedObjects.push({
            objectType: "Landholding",
            objectUuid: landholdingDraft.key,
          });
        }
      });

      if (formValues.companyDraft && (saveData || currentPage === DealJourneyNavigatorPages.ASSOCIATIONS)) {
        const createCollaboratorRes = await createCollaborator({
          addressCity: formValues.companyDraft.addressCity,
          addressCountryCode: formValues.companyDraft.addressCountryCode,
          addressLine1: formValues.companyDraft.addressLine1,
          addressLine2: formValues.companyDraft.addressLine2,
          addressPostcode: formValues.companyDraft.addressPostcode,
          addressRegion: formValues.companyDraft.addressRegion,
          associations: [],
          collaboratorType: formValues.companyDraft.collaboratorType,
          description: formValues.companyDraft.description,
          email: formValues.companyDraft.email,
          externalReference: formValues.companyDraft.crmId,
          name: formValues.companyDraft.name,
          organisationUuid: formValues.companyDraft.linkedOrganisationUuid,
          phone: formValues.companyDraft.phone,
          website: formValues.companyDraft.website,
          validateOnly: !saveData,
        });

        if (
          createCollaboratorRes.status === Status.Success &&
          createCollaboratorRes.data?.uuid !== undefined &&
          saveData
        ) {
          fetchedCompanyKey = createCollaboratorRes.data.uuid;
          localRelatedObjects.push({ objectType: "Collaborator", objectUuid: createCollaboratorRes.data.uuid });
        }

        if (createCollaboratorRes.status === Status.Error) {
          setErrors(createCollaboratorRes.errors);
          setCurrentPage(DealJourneyNavigatorPages.ASSOCIATIONS);
          setShowHoldingPage(false);
          return false;
        }
      }

      if (saveData) {
        // eslint-disable-next-line no-restricted-syntax
        for (const projectDraft of formValues.projectDrafts.filter(
          (draft) => !draft.projectUuid && !landholdingProjectKeys.has(draft.key)
        )) {
          // eslint-disable-next-line no-await-in-loop
          const createProjectResponse = await createProject({
            developerUuid:
              projectDraft.developerUuid === "chooseDeveloperLater" ? null : projectDraft.developerUuid || null,
            standardUuid: projectDraft.standardUuid,
            displayName: projectDraft.name,
            projectTypeUuid: projectDraft.projectTypeUuid,
            validateOnly: false,
            addressCountryCode: projectDraft.countryCode || null,
            addressPostcode: projectDraft.postcode || null,
            addressRegion: projectDraft.region || null,
            areaNetHa: projectDraft.areaNetHa ? Number(projectDraft.areaNetHa) : null,
            locationCoordinates:
              projectDraft.latitude || projectDraft.longitude
                ? `${projectDraft.latitude || ""},${projectDraft.longitude || ""}`
                : null,
          });

          if (createProjectResponse.status === Status.Error) {
            setErrors(createProjectResponse.errors);
            setCurrentPage(DealJourneyNavigatorPages.ASSOCIATIONS);
            setShowHoldingPage(false);
            return false;
          }

          if (createProjectResponse.data?.projectUuid) {
            fetchedProjectKeyMap[projectDraft.key] = createProjectResponse.data.projectUuid;
            localRelatedObjects.push({
              objectType: "Project",
              objectUuid: createProjectResponse.data.projectUuid,
            });
          }
        }
      }

      const dealSpecificData =
        formValues.structureInvestments && formValues.structureInvestments.length > 0
          ? mapInvestmentsToDealSpecificData(
              formValues.structureInvestments,
              localCompanyDraftKey,
              { ...persistedProjectKeyMap, ...fetchedProjectKeyMap },
              Array.from(landholdingProjectKeys),
              fetchedCompanyKey,
              [],
              saveData
            )
          : getEmptyDealSpecificData();

      const detailsFields = {
        conversionProbability: parsePercentageInputNullable(formValues.conversionProbability, null),
        dealTeam: getDealTeamWithLead(formValues.dealTeamUuids, formValues.dealLeadUuid),
        description: formValues.description ?? "",
        estimatedFees: Number(formValues.estimatedFees ?? 0),
        introductionDate: formValues.introductionDate ?? null,
        leadUserUuid: formValues.dealLeadUuid ?? "",
        name: formValues.dealName ?? "",
        pipelineUuid: formValues.dealPipelineUuid ?? "",
        projectedDpi: Number(formValues.projectedDpi ?? 0),
        source: formValues.dealSource ?? "",
        targetCloseDate: formValues.targetCloseDate ?? null,
      };

      const buildPageValidationPayload = (): Parameters<typeof createDeal>[0] => {
        const base = {
          ...detailsFields,
          // Only tell the backend a template is selected when validating the workflow page,
          // otherwise it validates template-related fields (e.g. stage dates) on earlier pages.
          isTemplateSelected: false,
          validateOnly: true,
          relatedObjects: [] as typeof localRelatedObjects,
          dealSpecificData: getEmptyDealSpecificData(),
          dealDates: mapDealWorkflowStageDates({}) as Parameters<typeof createDeal>[0]["dealDates"],
        };
        switch (currentPage) {
          case DealJourneyNavigatorPages.ASSOCIATIONS:
            return { ...base, relatedObjects: localRelatedObjects };
          case DealJourneyNavigatorPages.STRUCTURE:
            return { ...base, relatedObjects: localRelatedObjects, dealSpecificData };
          case DealJourneyNavigatorPages.WORKFLOW:
            return {
              ...base,
              isTemplateSelected: !!formValues.dealWorkflowTemplateUuid,
              relatedObjects: localRelatedObjects,
              dealSpecificData,
              dealDates: mapDealWorkflowStageDates(formValues.dealWorkflowStageDates) as Parameters<
                typeof createDeal
              >[0]["dealDates"],
            };
          default:
            return base;
        }
      };

      const dealPayload = saveData
        ? {
            ...detailsFields,
            isTemplateSelected: !!formValues.dealWorkflowTemplateUuid,
            dealDates: mapDealWorkflowStageDates(formValues.dealWorkflowStageDates) as Parameters<
              typeof createDeal
            >[0]["dealDates"],
            dealSpecificData,
            relatedObjects: localRelatedObjects,
            validateOnly: false,
          }
        : buildPageValidationPayload();

      const res = await createDeal(dealPayload);

      if (res.status === Status.Success && res.data) {
        const nextPage = getNextPage(currentPage);
        if (saveData) {
          if (formValues.dealWorkflowTemplateUuid && currentOrganisationUuid) {
            const applyWorkflowTemplateResponse = await applyWorkflowTemplate({
              objectUuid: res.data.uuid,
              organisations: [
                {
                  accessLevel: "Owner",
                  organisationUuid: currentOrganisationUuid,
                },
              ],
              workflowTemplateUuid: formValues.dealWorkflowTemplateUuid,
            });

            if (applyWorkflowTemplateResponse.status === Status.Error) {
              Toast.warning({
                message:
                  applyWorkflowTemplateResponse.errors?.[0]?.message ??
                  "Deal created, but the workflow template could not be applied.",
              });
            }
          }

          Toast.success({ message: "Deal created successfully" });
          removeStoredState();
          navigate(getDealListingRoute(res.data.uuid, OrganisationTypeConstants.ASSET_MANAGER));
        } else if (nextPage) {
          setCurrentPage(nextPage);
          setInProgressPage(nextPage);
        }
        return true;
      }

      setErrors(res.errors);
      return false;
    },
    [currentPage, formValues, getDealTeamWithLead, getNextPage]
  );

  const handleCreateDeal = async (): Promise<void> => {
    setShowHoldingPage(true);
    // Ensure all data is valid before saving any, requires additional api calls for validation round - seems fine as done on holding screen
    const isValid = await handleValidateAndNext(false);
    if (isValid) await handleValidateAndNext(true);
    else setShowHoldingPage(false);
  };

  useEffect(() => {
    setStoredState(formValues, inProgressPage);
  }, [inProgressPage, formValues]);

  useEffect(() => {
    if (hasCheckedDraft.current) return;
    hasCheckedDraft.current = true;

    if (initialDraftRaw) {
      setShowDraftModal(true);
    }
  }, [initialDraftRaw]);

  // Update assoiations based on search params when we've come from the investment directory
  useEffect(() => {
    const association = getAssociationFromSearchParams(searchParams);
    if (!association) return;

    setFormValues((prev) => mergeAssociationIntoFormValues(prev, association));
  }, [searchParams]);

  const handleUseDraft = useCallback(() => {
    // setSkipQueryAssociation(true);
    setShowDraftModal(false);
  }, []);

  const handleStartFresh = useCallback(() => {
    const association = getAssociationFromSearchParams(searchParams);

    removeStoredState();
    setFormValues(mergeAssociationIntoFormValues(defaultFormValues, association));
    setCurrentPage(DealJourneyNavigatorPages.DETAILS);
    setInProgressPage(DealJourneyNavigatorPages.DETAILS);
    // setSkipQueryAssociation(false);
    setShowDraftModal(false);
  }, [searchParams]);

  const memoisedValue = useMemo(
    () => ({
      showHoldingPage,
      inProgressPage,
      currentPage,
      errors,
      formValues,
      setFormValues,
      showDraftModal,
      handleUseDraft,
      handleStartFresh,
      getNextPage,
      handleFieldChange,
      handlePageChange,
      handleValidateAndNext,
      handleCreateDeal,
    }),
    [
      inProgressPage,
      currentPage,
      errors,
      formValues,
      handleFieldChange,
      handlePageChange,
      handleValidateAndNext,
      handleCreateDeal,
      showDraftModal,
      handleUseDraft,
      handleStartFresh,
    ]
  );

  return <NewDealJourneyContext.Provider value={memoisedValue}>{children}</NewDealJourneyContext.Provider>;
};
